import { ALCOHOL_DISCLAIMER, colors, MARKET } from '@nandosaus/constants';
import { observer } from 'mobx-react';
import { productPropTypes } from '@nandosaus/prop-types';
import { useRootStore } from '@nandosaus/state-management';
import styled from 'styled-components';
import { ImageFadeOverlay } from '@components/image-fade-overlay';
import { DaypartWarning } from '@components/daypart-warning';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { H4, P } from '@components/typography/new';
import { Box, Flex } from '../../grid';
import { DefaultImage } from '../../default';
import { Image } from '../../image';
import { useFeature } from '../../../hooks/use-feature';
import { useMarket } from '../../../hooks/use-market';
import { TextClamp } from '../../text-clamp';
import { Icon } from '../../icons';

const ReadMoreButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  justify-content: center;
  width: 100%;
  line-height: 1;
`;

const ReadMore = ({ children, lines }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggle = () => setIsExpanded(prev => !prev);

  if (!children) {
    return null;
  }

  const requiresTruncation = children.length > 100;

  const text = (
    <P whiteSpace="pre-wrap" textAlign="left" color="white">
      {children}
    </P>
  );

  return (
    <div>
      {isExpanded || !requiresTruncation ? (
        text
      ) : (
        <>
          <TextClamp lines={lines}>{text}</TextClamp>
          <Box mb={{ xs: '18px', md: '16px' }} />
        </>
      )}
      {requiresTruncation && (
        <ReadMoreButton onClick={toggle}>
          <Icon name="chevron" fill="white" rotate={isExpanded ? '180deg' : '0deg'} />
        </ReadMoreButton>
      )}
    </div>
  );
};

ReadMore.propTypes = {
  children: PropTypes.node.isRequired,
  lines: PropTypes.number,
};

ReadMore.defaultProps = {
  lines: 2,
};

const PerksContainer = styled.div`
  position: absolute;
  display: flex;
  direction: row;
  align-items: center;
  justify-content: center;
  z-index: 2;
  top: 20px;
  left: 20px;
  background-color: black;
  border-radius: 4px;
  padding: 8px 12px;
  gap: 8px;
`;

const ProductDetailHeader = observer(({ product, points, showPerksSash }) => {
  const { ProductDetailsState, MenuStore, OrderingContextStore } = useRootStore();
  const { isCateringMenu } = MenuStore;

  const market = useMarket();

  const isAustralianMarket = market === MARKET.AUSTRALIA;

  const sizePickerEnabled = useFeature('product-size-picker');
  const showPrice = !sizePickerEnabled || (product.hasOtherSizes !== undefined && !product.hasOtherSizes);

  const { disclaimer, isAlcoholProduct } = ProductDetailsState;
  const showProductDetails = showPrice || isAustralianMarket || product.formattedDietaryPreferencesList;

  // Find the category for this product to check for daypart
  const productCategory = product?.id ? MenuStore.getProductCategory(product.id) : null;
  const daypart = productCategory?.daypart;
  const timezone = OrderingContextStore.restaurant?.timezone;

  return (
    <>
      <Box position="relative">
        {points && showPerksSash && (
          <PerksContainer>
            <Box>
              <img alt="Peri Perks logo" src="/static/images/brand/peri-perks-logo-mini.svg" width="30" />
            </Box>
            <P color={colors.white}>Earn {points} points</P>
          </PerksContainer>
        )}
        <ImageFadeOverlay height="100px">
          {product.image ? (
            <Box position="relative">
              <StyledImage src={product.image} alt={product.name} width={576} height={350} objectFit="cover" />
            </Box>
          ) : (
            <DefaultImage height="350px" />
          )}
        </ImageFadeOverlay>
      </Box>

      <Box px={{ xs: '20px', md: '40px' }} pb={{ xs: '12px', md: '32px' }} bg={colors.black}>
        <Box>
          {!isCateringMenu && <DaypartWarning onDarkBg mb="36px" daypart={daypart} timezone={timezone} />}

          <H4 mt={-1} mb="12px" zIndex={2} position="relative" color="white">
            {product.name}
          </H4>
          {showProductDetails && (
            <Flex mb="16px" justifyContent="left" alignItems="center" gap="4px">
              {showPrice && product.prices.formattedPrice && (
                <P color="white" fontWeight="bold">
                  {product.prices.formattedPrice}
                </P>
              )}
              {showPrice && product.prices.formattedPrice && product.formattedKilojoules && <P color="white">/</P>}
              {product.formattedKilojoules && (
                <P textAlign="center" color="white">
                  {product.formattedKilojoules}
                </P>
              )}
              {product.formattedServes && isCateringMenu && (
                <>
                  <P color="white">|</P>
                  <P textAlign="center" color="white">
                    {product.formattedServes}
                  </P>
                </>
              )}
              {product.formattedDietaryPreferencesList && (
                <Box display="flex" gap="8px" pl="18px">
                  {product.formattedDietaryPreferencesList.map(dietaryPreference => (
                    <Flex
                      backgroundColor="warmWhite3"
                      borderRadius="4px"
                      px="8px"
                      py="4px"
                      variant="extra-small-semi-bold"
                    >
                      <P variant="extra-small-semi-bold">{dietaryPreference}</P>
                    </Flex>
                  ))}
                </Box>
              )}
            </Flex>
          )}

          {isAustralianMarket && (
            <P variant="small" mb="10px" color="grey40" whiteSpace="pre-wrap">
              {isAlcoholProduct ? `${ALCOHOL_DISCLAIMER}\n\n${disclaimer}` : disclaimer}
            </P>
          )}
          {product.description && <ReadMore lines={2}>{product.description}</ReadMore>}
        </Box>
      </Box>
    </>
  );
});

const StyledImage = styled(Image)`
  width: 100%;
`;

ProductDetailHeader.propTypes = {
  product: productPropTypes.isRequired,
  showPerksSash: PropTypes.bool,
  points: PropTypes.string,
};

ProductDetailHeader.defaultProps = {
  showPerksSash: false,
  points: undefined,
};

export { ProductDetailHeader };
